import type { RiskSeverity } from "@/lib/api";

/**
 * What each severity actually MEANS, written once and shown wherever a
 * severity is chosen or displayed.
 *
 * A severity that is only a word is a severity nobody can choose correctly.
 * "Medium" and "high" tell an HR user nothing about what they are about to do
 * to a colleague, so the picker states the consequence next to each option and
 * the list repeats it against every flag. The wording here is the commitment
 * the tool makes; if the controls change, this file changes with them, and the
 * screens follow.
 *
 * The distinction that matters is between watching and restricting:
 *
 * * low and medium are **observation**. Nothing about the person's access
 *   changes. They differ in how closely SecOps looks, not in what the person
 *   can do.
 * * high **restricts the person's working day**, and they will notice. That is
 *   a decision with a cost, and the screen says so before it is taken rather
 *   than after an upload refuses, or a USB stick stops taking files, in
 *   front of a customer.
 */

export type SeverityMeaning = {
  label: string;
  /** One line, for the picker and the list. */
  summary: string;
  /** What actually changes for the person. Empty where nothing does. */
  effects: string[];
  /** Said out loud where the choice restricts somebody. */
  caution?: string;
};

export const SEVERITY: Record<RiskSeverity, SeverityMeaning> = {
  low: {
    label: "Low",
    summary: "Watch only. Nothing changes for the person.",
    effects: [
      "The person appears in the monitored population for SecOps.",
      "No control is applied and nothing is restricted.",
    ],
  },
  medium: {
    label: "Medium",
    summary: "Heightened monitoring. Nothing changes for the person.",
    effects: [
      "The person is monitored in a heightened state: their sign-ins, downloads and mail flow are reviewed more closely.",
      "No control is applied and nothing is restricted. They will not notice a difference.",
    ],
  },
  high: {
    label: "High",
    summary: "Restricts what the person can do, and they will notice.",
    effects: [
      "Uploading files to Dropbox, WeTransfer, Google Drive, private webmail, Slack, Facebook and AI assistants such as ChatGPT, Gemini and Claude is blocked on their managed devices.",
      "Microsoft Copilot still works: it is the licensed one and stays inside the tenant.",
      "Copying files to a USB stick or other removable drive is blocked.",
      "Mail attachments over 10 MB to recipients outside Norican are blocked. Internal mail is untouched.",
      "Downloading and reading are deliberately left alone, so they can carry on working.",
      "Everything medium does, as well: they stay in the monitored population.",
    ],
    caution:
      "These controls take effect on a working colleague who has not been accused of anything, and they are visible to them. Use high when there is a specific concern that justifies it, tell their manager, and set an end date you are willing to defend. The flag expires on its own; the restrictions lift with it.",
  },
};

/** The order the picker offers them in: least to most restrictive. */
export const SEVERITY_ORDER: RiskSeverity[] = ["low", "medium", "high"];

/** True where choosing this severity changes what the person can do. */
export function restricts(severity: RiskSeverity): boolean {
  return severity === "high";
}
